"use client";

import { useState } from "react";
import { Check, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";

interface AdditionalInfo {
  id?: string;
  product_id?: string;
  required_notices?: string[];
  included_items?: string[];
  precautions?: string[];
}

interface ProductAdditionalInfoProps {
  additionalInfo?: AdditionalInfo;
  inclusions?: string[];
  exclusions?: string[];
  notes?: string[];
  requirements?: string[];
}

interface AccordionSectionProps {
  title: string;
  items: string[];
  defaultOpen?: boolean;
}

function AccordionSection({
  title,
  items,
  defaultOpen = true,
}: AccordionSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (!items || items.length === 0) return null;

  return (
    <div>
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-3 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <Check className="h-5 w-5 text-blue-500" />
          <span className="font-semibold text-gray-900">{title}</span>
        </div>
        <ChevronUp
          className={cn(
            "h-5 w-5 text-gray-400 transition-transform duration-200",
            !isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Content - 회색 배경 */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-200",
          isOpen ? "max-h-[500px] pb-4" : "max-h-0"
        )}
      >
        <div className="space-y-3 rounded-xl bg-gray-50 p-4">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <Check className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
              <span className="min-w-0 wrap-break-word text-sm leading-6 text-gray-700">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ProductAdditionalInfo({
  additionalInfo,
  inclusions,
  exclusions,
  notes,
  requirements,
}: ProductAdditionalInfoProps) {
  const t = useTranslations("product.additionalInfo");

  // Use localized fields if available, otherwise fallback to additional_info
  const includedItems =
    inclusions && inclusions.length > 0
      ? inclusions
      : additionalInfo?.included_items;
  const precautionItems =
    notes && notes.length > 0 ? notes : additionalInfo?.precautions;
  const requiredNotices =
    requirements && requirements.length > 0
      ? requirements
      : additionalInfo?.required_notices;

  const hasIncludedItems = includedItems && includedItems.length > 0;
  const hasPrecautions = precautionItems && precautionItems.length > 0;
  const hasRequiredNotices = requiredNotices && requiredNotices.length > 0;
  const hasExclusions = exclusions && exclusions.length > 0;

  if (
    !hasIncludedItems &&
    !hasPrecautions &&
    !hasRequiredNotices &&
    !hasExclusions
  ) {
    return null;
  }

  return (
    <div className="py-4">
      {/* 필수 안내사항 */}
      {hasRequiredNotices && (
        <AccordionSection
          title={t("requiredNotices")}
          items={requiredNotices || []}
          defaultOpen={true}
        />
      )}

      {/* 주의사항 */}
      {hasPrecautions && (
        <AccordionSection
          title={t("precautions")}
          items={precautionItems || []}
          defaultOpen={true}
        />
      )}

      {/* 포함사항 */}
      {hasIncludedItems && (
        <AccordionSection
          title={t("includedItems")}
          items={includedItems || []}
          defaultOpen={true}
        />
      )}

      {/* 불포함사항 */}
      {hasExclusions && (
        <AccordionSection
          title={t("excludedItems")}
          items={exclusions || []}
          defaultOpen={true}
        />
      )}
    </div>
  );
}
